'use client';

import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface BreadcrumbItem {
  label: string;
  path: string;
  isActive?: boolean;
}

interface BreadcrumbTrailProps {
  customItems?: BreadcrumbItem[];
  showHome?: boolean;
}

const BreadcrumbTrail = ({ customItems, showHome = true }: BreadcrumbTrailProps) => {
  const pathname = usePathname();

  const getDefaultBreadcrumbs = (): BreadcrumbItem[] => {
    const items: BreadcrumbItem[] = [];

    if (showHome) {
      items.push({ label: 'Home', path: '/home' });
    }

    switch (pathname) {
      case '/input-form':
        items.push(
          { label: 'Create Plan', path: '/input-form' },
          { label: 'Space Assessment', path: '/input-form', isActive: true }
        );
        break;
      case '/ai-plan':
        items.push(
          { label: 'Create Plan', path: '/input-form' },
          { label: 'Space Assessment', path: '/input-form' },
          { label: 'AI Recommendations', path: '/ai-plan', isActive: true }
        );
        break;
      case '/saved-plans':
        items.push({ label: 'My Plans', path: '/saved-plans', isActive: true });
        break;
      case '/login':
        items.push({ label: 'Sign In', path: '/login', isActive: true });
        break;
      case '/register':
        items.push({ label: 'Create Account', path: '/register', isActive: true });
        break;
      default:
        if (pathname !== '/home') {
          items.push({ label: 'Current Page', path: pathname, isActive: true });
        }
    }

    return items;
  };

  const breadcrumbs = customItems || getDefaultBreadcrumbs();

  if (breadcrumbs.length <= 1 && pathname === '/home') {
    return null;
  }

  return (
    <nav className="flex items-center space-x-2 text-sm text-muted-foreground mb-6" aria-label="Breadcrumb">
      <ol className="flex items-center space-x-2">
        {breadcrumbs.map((item, index) => (
          <li key={item.path} className="flex items-center">
            {index > 0 && (
              <Icon 
                name="ChevronRightIcon" 
                size={16} 
                className="mx-2 text-muted-foreground/60" 
              />
            )}
            {item.isActive ? (
              <span className="text-foreground font-medium">{item.label}</span>
            ) : (
              <Link
                href={item.path}
                className="hover:text-foreground transition-colors duration-200"
              >
                {item.label}
              </Link>
            )}
          </li>
        ))}
      </ol>
    </nav>
  );
};

export default BreadcrumbTrail;